import axios from 'axios';

// Force HTTPS for Railway production deployment
const getApiUrl = (): string => {
  // Get the environment variable (might be HTTP or HTTPS)
  let apiUrl = process.env.REACT_APP_API_URL || '';

  // CRITICAL: Force HTTPS for Railway deployments
  // Check if we're on Railway either by hostname or if the API URL contains railway.app
  const isRailway = (typeof window !== 'undefined' && window.location.hostname.includes('railway.app')) ||
                    apiUrl.includes('railway.app');

  if (isRailway) {
    // If the environment variable exists and contains railway.app, upgrade it to HTTPS
    if (apiUrl && apiUrl.includes('railway.app')) {
      apiUrl = apiUrl.replace('http://', 'https://');
      console.log('🔒 Upgraded Railway API URL to HTTPS:', apiUrl);
      return apiUrl;
    }
    // Fallback to hardcoded HTTPS URL if no env variable
    return 'https://patient-communication-backend.up.railway.app';
  }

  // For local development
  if (typeof window !== 'undefined' && window.location.protocol === 'https:') {
    // If frontend is HTTPS, backend should be HTTPS too
    const envUrl = apiUrl || 'http://localhost:8000';
    return envUrl.replace('http://', 'https://');
  }

  // Default: use environment variable or localhost
  return apiUrl || 'http://localhost:8000';
};

const API_URL = getApiUrl();

console.log('🔧 API Client initialized with baseURL:', API_URL);

// Create axios instance with baseURL pre-configured
export const apiClient = axios.create({
  baseURL: API_URL,
  // Don't set default Content-Type - let axios auto-detect based on data type
  // (FormData will use multipart/form-data, objects will use application/json)
});

// Add interceptor to force HTTPS on Railway AND ensure auth token is always included
apiClient.interceptors.request.use(
  function (config) {
    // CRITICAL: Always add token from localStorage to ensure authentication works
    // This prevents race conditions where AuthContext hasn't set the header yet
    const token = localStorage.getItem('token');
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
      console.log('🔑 Request interceptor added token to:', config.url);
    } else {
      console.warn('⚠️ No token found in localStorage for request:', config.url);
    }

    // Check if we're on Railway
    const isRailwayRuntime = typeof window !== 'undefined' && window.location.hostname.includes('railway.app');

    // Also check if the request URL contains railway.app
    const requestToRailway = config.baseURL?.includes('railway.app') || config.url?.includes('railway.app');

    if (isRailwayRuntime || requestToRailway) {
      // Force HTTPS for baseURL
      if (config.baseURL && config.baseURL.startsWith('http://')) {
        config.baseURL = config.baseURL.replace('http://', 'https://');
        console.warn('⚠️ Interceptor upgraded baseURL from HTTP to HTTPS:', config.baseURL);
      }

      // Force HTTPS for absolute URLs
      if (config.url && config.url.startsWith('http://')) {
        config.url = config.url.replace('http://', 'https://');
        console.warn('⚠️ Interceptor upgraded URL from HTTP to HTTPS:', config.url);
      }

      // Construct full URL for logging
      const fullUrl = config.url?.startsWith('http')
        ? config.url
        : `${config.baseURL || ''}${config.url || ''}`;

      // Final verification
      if (fullUrl.startsWith('http://') && fullUrl.includes('railway.app')) {
        console.error('🚨 CRITICAL: HTTP request detected to Railway! URL:', fullUrl);
        console.error('Config:', { baseURL: config.baseURL, url: config.url });
      }
    }

    return config;
  },
  function (error) {
    return Promise.reject(error);
  }
);

// Also export the API URL for compatibility
export const API_BASE_URL = API_URL;
